// ============================================================================
// Sincronización del tema DevExtreme con el atributo data-theme del <html>
// (Tarea 8.4 / Req 8.3). Un MutationObserver observa <html> y aplica el tema
// base de DevExtreme (light/dark) al cambiar. Los acentos (azul/rojo/violeta/
// moon) los aportan las variables CSS de themes.css, no un tema DevExtreme.
// ============================================================================
import themes from 'devextreme/ui/themes';

function resolveDxTheme(theme: string): string {
  return theme === 'dark' ? 'fluent.blue.dark' : 'fluent.blue.light';
}

/**
 * Aplica el tema DevExtreme de forma segura:
 * - Solo cambia si el tema destino está realmente registrado (evita E0021).
 * - Nunca deja que un error de temas rompa el arranque de la app.
 */
function applyDxThemeSafe(): void {
  try {
    const theme = document.documentElement.getAttribute('data-theme') || 'light';
    const target = resolveDxTheme(theme);

    // themes.current() como getter devuelve el tema activo; si es null, aún no hay tema listo.
    let currentTheme: string | null = null;
    try { currentTheme = (themes as any).current?.() ?? null; } catch { currentTheme = null; }

    // Solo intentamos fijar el tema si es distinto al actual. Envuelto para no propagar E0021.
    if (currentTheme !== target) {
      try {
        (themes as any).current(target);
      } catch {
        // El tema destino no está registrado (CSS no cargado). Mantener el actual.
      }
    }
  } catch {
    // Cualquier fallo de temas es no-fatal: la UI usa las variables CSS de themes.css igualmente.
  }
}

export function initDevExtremeThemeSync(): () => void {
  const observer = new MutationObserver(() => applyDxThemeSafe());
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme', 'data-accent'],
  });

  // Esperar a que DevExtreme tenga los temas listos antes de aplicar.
  // Usamos SOLO themes.ready(): evita el warning W0004 (timeout de carga de tema)
  // que se dispara al llamar themes.current() antes de que el CSS del tema esté registrado.
  let readyHandled = false;
  try {
    (themes as any).ready?.(() => { readyHandled = true; applyDxThemeSafe(); });
  } catch { /* noop */ }

  // Fallback: si ready() no existe o no disparó, aplicar de forma diferida (no inmediata)
  // para dar tiempo a que los <link rel="dx-theme"> carguen y no provocar W0004.
  setTimeout(() => { if (!readyHandled) { applyDxThemeSafe(); } }, 0);

  return () => observer.disconnect();
}
